import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Loading, Empty } from "../components/Layout";
import { BandBadge } from "../components/Badges";
import AllocationRulesEditor from "../components/AllocationRulesEditor";
import { toast } from "sonner";
import { SlidersHorizontal, CheckCheck, Info } from "lucide-react";

function Breakdown({ s }) {
  if (!s?.breakdown) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1" data-testid="suggestion-breakdown">
      {Object.entries(s.breakdown).map(([k, v]) => (
        <span key={k} className={`font-mono text-[8px] uppercase px-1 border ${v > 0 ? "border-[#2F7A3E]/40 text-[#2F7A3E]" : "border-[#DFDAD0] text-[#1C2530]/30"}`}>{k.replace("_", " ")} {v}</span>
      ))}
    </div>
  );
}

export default function AllocationQueue() {
  const { user } = useAuth();
  const [leads, setLeads] = useState(null);
  const [elig, setElig] = useState({});
  const [reps, setReps] = useState([]);
  const [showRules, setShowRules] = useState(false);
  const [showAlt, setShowAlt] = useState({});
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const { data } = await api.get("/queue/allocation");
    setLeads(data);
    const map = {};
    await Promise.all(data.slice(0, 60).map(async (l) => {
      try { const r = await api.get(`/leads/${l.id}/eligible-reps`); map[l.id] = Object.fromEntries(r.data.reps.map((rp) => [rp.id, rp.covered])); }
      catch { map[l.id] = {}; }
    }));
    setElig(map);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/users").then((r) => setReps(r.data.filter((u) => u.role === "rep"))); }, []);

  const assign = async (leadId, repId) => {
    if (!repId) return;
    try { const { data } = await api.post(`/leads/${leadId}/assign`, { assigned_to: repId }); if (data?.pending_approval) toast.info(data.message); else toast.success("Lead assigned"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Assign failed"); }
  };

  const suggestedRows = (leads || []).filter((l) => !l.assigned_to && l.suggested_rep?.id);
  const acceptAll = async () => {
    if (!window.confirm(`Assign ${suggestedRows.length} lead${suggestedRows.length === 1 ? "" : "s"} to their suggested reps?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/allocation/bulk-assign-suggested", { assignments: suggestedRows.map((l) => ({ lead_id: l.id, rep_id: l.suggested_rep.id })) });
      toast.success(`${data.assigned} assigned${data.pending ? `, ${data.pending} sent for approval` : ""}${data.failed ? `, ${data.failed} failed` : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk assign failed"); } finally { setBusy(false); }
  };

  if (!leads) return (<><PageHeader eyebrow="SUPERVISOR" title="Allocation Queue" /><div className="p-8"><Loading /></div></>);

  return (
    <>
      <PageHeader eyebrow="SUPERVISOR · SUGGESTIONS SCORED BY YOUR ALLOCATION RULES · COVERAGE-GATED" title="Allocation Queue"
        right={<div className="flex items-center gap-2">
          <button onClick={() => setShowRules((s) => !s)} data-testid="alloc-rules-toggle" className="flex items-center gap-1.5 border border-[#DFDAD0] px-3 py-1.5 font-mono text-[10px] uppercase hover:bg-[#EFECE5]"><SlidersHorizontal className="w-3.5 h-3.5" /> Rules</button>
          <button onClick={acceptAll} disabled={busy || suggestedRows.length === 0} data-testid="alloc-accept-all" className="flex items-center gap-1.5 bg-[#2F7A3E] text-white px-3 py-1.5 font-mono text-[10px] uppercase hover:bg-[#256030] disabled:opacity-40"><CheckCheck className="w-3.5 h-3.5" /> Accept all suggestions ({suggestedRows.length})</button>
        </div>} />
      <div className="p-8 space-y-5">
        {showRules && <AllocationRulesEditor canEdit={user.role === "admin"} onClose={() => setShowRules(false)} onSaved={load} />}
        <div className="flex items-start gap-2 border-l-2 border-[#3E8F99] bg-[#3E8F99]/5 px-4 py-2.5 text-xs text-[#1C2530]/75" data-testid="alloc-explainer">
          <Info className="w-3.5 h-3.5 text-[#3E8F99] mt-0.5 shrink-0" />
          <span>Each unassigned lead gets a suggested rep scored on coverage, region, industry, deal size, workload and seniority — weights and rep profiles live under <span className="font-medium">Rules</span>. Suggestions never auto-assign; you approve one-by-one or accept all.</span>
        </div>
        {leads.length === 0 ? <Empty label="ALLOCATION_QUEUE_EMPTY" /> : (
          <div className="border border-[#DFDAD0] bg-white overflow-x-auto" data-testid="allocation-queue-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#EFECE5] font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">
                  <th className="text-left px-4 py-2.5">Account</th>
                  <th className="text-left px-4 py-2.5">Region</th>
                  <th className="text-left px-4 py-2.5">Stage</th>
                  <th className="text-left px-4 py-2.5">Band</th>
                  <th className="text-left px-4 py-2.5">Assigned</th>
                  <th className="text-left px-4 py-2.5">Suggestion & assign</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l, i) => {
                  const cov = elig[l.id] || {};
                  const s = l.suggested_rep;
                  return (
                    <tr key={l.id} className={`border-t border-[#DFDAD0] ${i % 2 ? "bg-[#F5F3EE]/40" : "bg-white"}`} data-testid={`alloc-row-${l.id}`}>
                      <td className="px-4 py-2.5">
                        <button onClick={() => nav(`/leads/${l.id}`)} className="font-medium text-[#1C2530] hover:underline">{l.account?.name}</button>
                        {l.is_event_triggered && <span className="ml-2 font-mono text-[9px] uppercase px-1.5 py-0.5 bg-[#E50914]/10 text-[#E50914]" data-testid={`event-badge-${l.id}`}>Event</span>}
                        <div className="text-xs text-[#1C2530]/50">{l.contact?.name}{l.account?.industry ? ` · ${l.account.industry}` : ""}</div>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs">{l.region}</td>
                      <td className="px-4 py-2.5 font-mono text-xs">{l.stage}</td>
                      <td className="px-4 py-2.5"><BandBadge band={l.band} score={l.total_score} /></td>
                      <td className="px-4 py-2.5 text-xs text-[#1C2530]/70">{l.assignee_name || "—"}</td>
                      <td className="px-4 py-2.5 min-w-[280px]">
                        {s && s.id && (
                          <div className="mb-1.5" data-testid={`suggested-rep-${l.id}`}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-[10px] text-[#3E8F99]">Suggested: {s.name} · score {s.score} · {s.open_leads} open</span>
                              <button onClick={() => assign(l.id, s.id)} data-testid={`approve-suggestion-${l.id}`}
                                className="border border-[#2F7A3E] text-[#2F7A3E] px-2 py-0.5 font-mono text-[9px] uppercase hover:bg-[#2F7A3E]/10">Approve</button>
                              {s.alternatives?.length > 0 && <button onClick={() => setShowAlt((a) => ({ ...a, [l.id]: !a[l.id] }))} data-testid={`alt-toggle-${l.id}`} className="font-mono text-[9px] uppercase text-[#1C2530]/50 hover:underline">{showAlt[l.id] ? "hide" : "alternatives"}</button>}
                            </div>
                            <Breakdown s={s} />
                            {showAlt[l.id] && s.alternatives?.map((a) => (
                              <div key={a.id} className="flex items-center gap-2 mt-1 text-[10px] font-mono text-[#1C2530]/60" data-testid={`alt-${l.id}-${a.id}`}>
                                <span>{a.name} · {a.score}{a.covered ? "" : " · no coverage"}</span>
                                <button onClick={() => assign(l.id, a.id)} disabled={!a.covered} className="border border-[#DFDAD0] px-1.5 text-[9px] uppercase hover:bg-[#EFECE5] disabled:opacity-30">Assign</button>
                              </div>
                            ))}
                          </div>
                        )}
                        {s && !s.id && <div className="mb-1.5 font-mono text-[10px] text-[#E2711D]" data-testid={`no-suggestion-${l.id}`}>{s.reason} (best {s.score})</div>}
                        <select data-testid={`assign-select-${l.id}`} defaultValue="" onChange={(e) => assign(l.id, e.target.value)}
                          className="border border-[#DFDAD0] bg-white text-xs px-2 py-1 outline-none focus:border-[#1C2530]">
                          <option value="">Select rep…</option>
                          {reps.map((r) => { const covered = cov[r.id]; return <option key={r.id} value={r.id} disabled={covered === false}>{r.name} {covered === false ? "— no coverage now" : covered ? "✓ covered" : ""}</option>; })}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
